(() => {
  if (customElements.get('comic-share')) return;

  class ComicShare extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      if (document.readyState === 'loading' && !this.querySelector('[data-share-link]')) {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), {
          once: true,
          signal: this.controller.signal,
        });
      } else {
        this.initialize();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.downloadController?.abort();
      clearTimeout(this.downloadTimer);
      clearTimeout(this.statusTimer);
    }

    initialize() {
      this.status = this.querySelector('[data-share-status]');
      this.fallback = this.querySelector('[data-copy-fallback]');
      this.input = this.querySelector('[data-share-link]');
      this.instagram = this.querySelector('[data-instagram-panel]');
      this.instagramToggle = this.querySelector('[data-share-action="instagram"]');
      if (!this.status || !this.fallback || !this.input || !this.instagram) return;

      try {
        const url = new URL(this.dataset.url, document.baseURI);
        if (!['http:', 'https:'].includes(url.protocol)) return;
        url.search = '';
        url.hash = '';
        this.shareUrl = url.href;
      } catch {
        return;
      }

      this.input.value = this.shareUrl;
      this.querySelectorAll('[data-share-action]').forEach((button) => {
        button.disabled = false;
      });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-share-action]');
        if (!button || !this.contains(button) || button.disabled) return;
        if (button.dataset.shareAction === 'instagram') this.toggleInstagram();
        if (button.dataset.shareAction === 'copy') this.copyLink();
        if (button.dataset.shareAction === 'download') this.saveCover(button);
      }, { signal: this.controller.signal });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.instagram.hidden) return;
        this.instagram.hidden = true;
        this.instagramToggle.setAttribute('aria-expanded', 'false');
        this.instagramToggle.focus();
        event.stopPropagation();
      }, { signal: this.controller.signal });
    }

    announce(message) {
      clearTimeout(this.statusTimer);
      this.status.textContent = '';
      this.statusTimer = setTimeout(() => {
        if (this.isConnected) this.status.textContent = message;
      }, 20);
    }

    toggleInstagram() {
      const expanded = this.instagram.hidden;
      this.instagram.hidden = !expanded;
      this.instagramToggle.setAttribute('aria-expanded', String(expanded));
      this.status.textContent = '';
      this.fallback.hidden = true;
    }

    async copyLink() {
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(this.shareUrl);
        if (!this.isConnected) return;
        this.fallback.hidden = true;
        this.announce(this.dataset.copied);
      } catch {
        if (!this.isConnected) return;
        this.fallback.hidden = false;
        this.input.focus();
        this.input.select();
        this.input.setSelectionRange(0, this.input.value.length);
        this.announce(this.dataset.copyFailed);
      }
    }

    async saveCover(button) {
      if (!this.dataset.image || this.downloadController) return;
      const fallback = this.querySelector('[data-cover-fallback]');
      const controller = new AbortController();
      this.downloadController = controller;
      this.downloadTimer = setTimeout(() => controller.abort(), 15000);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.announce(this.dataset.downloading);
      if (fallback) fallback.hidden = true;

      try {
        const response = await fetch(this.dataset.image, {
          credentials: 'omit',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Cover unavailable');
        const blob = await response.blob();
        const extensions = {
          'image/jpeg': 'jpg',
          'image/png': 'png',
          'image/webp': 'webp',
          'image/avif': 'avif',
          'image/gif': 'gif',
        };
        const extension = extensions[blob.type];
        if (!extension || !blob.size) throw new Error('Invalid cover');
        if (!this.isConnected) return;
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const name = (this.dataset.title || 'comic').normalize('NFKD')
          .replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').slice(0, 80) || 'comic';
        link.href = objectUrl;
        link.download = `${name}-cover.${extension}`;
        link.hidden = true;
        this.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        this.announce(this.dataset.downloadStarted);
      } catch {
        if (!this.isConnected) return;
        if (fallback) fallback.hidden = false;
        this.announce(this.dataset.downloadFailed);
      } finally {
        clearTimeout(this.downloadTimer);
        this.downloadController = null;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('comic-share', ComicShare);
})();
